import React, { useEffect, useRef } from "react";
import L from "leaflet";
import "leaflet.markercluster";
import { symbolSvg } from "../lib/symbols";

const CATEGORY_LABELS = {
  en: {
    domestic: "Domestic",
    sexual: "Sexual",
    harassment: "Harassment",
    psychological: "Psychological",
    other: "Other",
  },
  ru: {
    domestic: "Домашнее",
    sexual: "Сексуальное",
    harassment: "Домогательства",
    psychological: "Психологическое",
    other: "Другое",
  },
};

function escapeHtml(s = "") {
  return s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
}

export function MapView({ stories, tempPoint, onMapClick, lang }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const clusterRef = useRef(null);
  const tempMarkerRef = useRef(null);

  // init map once
  useEffect(() => {
    if (mapRef.current || !containerRef.current) return;
    const map = L.map(containerRef.current, {
      center: [30, 10],
      zoom: 2,
      minZoom: 2,
      maxZoom: 16,
      worldCopyJump: true,
      zoomControl: true,
      attributionControl: true,
    });
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: '&copy; OpenStreetMap contributors',
      subdomains: "abc",
      maxZoom: 19,
    }).addTo(map);

    const cluster = L.markerClusterGroup({
      showCoverageOnHover: false,
      maxClusterRadius: 48,
      iconCreateFunction: (c) =>
        L.divIcon({
          html: `<div style="width:38px;height:38px">${c.getChildCount()}</div>`,
          className: "voice-cluster",
          iconSize: [38, 38],
        }),
    });
    map.addLayer(cluster);
    clusterRef.current = cluster;
    mapRef.current = map;

    map.on("click", (e) => {
      onMapClickRef.current && onMapClickRef.current(e.latlng);
    });

    setTimeout(() => map.invalidateSize(), 200);
    return () => {
      map.remove();
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // keep latest onMapClick without re-init
  const onMapClickRef = useRef(onMapClick);
  useEffect(() => {
    onMapClickRef.current = onMapClick;
  }, [onMapClick]);

  // render markers when stories/lang change
  useEffect(() => {
    const cluster = clusterRef.current;
    if (!cluster) return;
    cluster.clearLayers();
    const labels = CATEGORY_LABELS[lang] || CATEGORY_LABELS.en;
    stories.forEach((s) => {
      const icon = L.divIcon({
        html: symbolSvg(s.symbol, { size: 24 }),
        className: "voice-marker",
        iconSize: [24, 24],
        iconAnchor: [12, 12],
        popupAnchor: [0, -12],
      });
      const marker = L.marker([s.lat, s.lng], { icon });
      const date = new Date(s.created_at).toLocaleDateString(lang === "ru" ? "ru-RU" : "en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
      const html = `
        <div style="max-width:240px">
          <div style="display:flex;align-items:center;gap:8px;margin-bottom:8px">
            <span style="color:#B47B84">${symbolSvg(s.symbol, { size: 18 })}</span>
            <span style="font-size:11px;font-weight:600;letter-spacing:.03em;text-transform:uppercase;color:#B47B84">${labels[s.category] || ""}</span>
          </div>
          ${
            s.text && s.text.trim()
              ? `<p style="margin:0;font-size:14px;line-height:1.5;color:#2A2A2A">${escapeHtml(s.text)}</p>`
              : `<p style="margin:0;font-size:14px;font-style:italic;color:#9CA3AF">anonymous</p>`
          }
          <div style="margin-top:10px;font-size:12px;color:#6B7280">${escapeHtml(s.nickname || "Anonymous")} · ${date}</div>
        </div>`;
      marker.bindPopup(html, { closeButton: true });
      cluster.addLayer(marker);
    });
  }, [stories, lang]);

  // temp pulsing point
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (tempMarkerRef.current) {
      map.removeLayer(tempMarkerRef.current);
      tempMarkerRef.current = null;
    }
    if (tempPoint) {
      const icon = L.divIcon({ html: '<div class="place-pulse"></div>', className: "", iconSize: [18, 18], iconAnchor: [9, 9] });
      const m = L.marker([tempPoint.lat, tempPoint.lng], { icon, zIndexOffset: 1000 });
      m.addTo(map);
      tempMarkerRef.current = m;
    }
  }, [tempPoint]);

  return <div ref={containerRef} data-testid="map-canvas" className="h-full w-full" />;
}
